import {Separator} from 'platform/components';
import {
  Box,
  Grid,
  GridItem,
  HStack,
  Show,
  Space,
  Text,
  TextAlign,
  ThemeColorPath,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {isLastIndexInArray, suffixTestId, TestIdProps, useDimensions} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {ColorSegment} from '../../../components/ColorSegment';
import {useIsPrintForced} from '../../../hooks/useIsPrintForced';
import {
  type ChartSegmentColor,
  ColorSegmentColor,
  NeutralColorSegment,
} from '../../../types/ColorSegmentColor';
import {AttractivenessMarker} from './AttractivenessMarker';

const CHART_PERCENTAGE_MARKS = [0, 20, 40, 60, 80, 100];

type Segment = {
  key: string;
  color: ChartSegmentColor;
  backgroundColor?: ThemeColorPath;
  label: string;
  secondaryLabel?: string;
};

interface AttractivenessChartProps extends TestIdProps {
  value?: number;
}

export function AttractivenessChart(props: AttractivenessChartProps) {
  const {getResponsiveValue} = useIsPrintForced();
  const [chartRef, chartDimensions] = useDimensions();

  const isUnknown = isNil(props.value);
  const segments: Segment[] = [
    {
      key: 'veryLow',
      color: isUnknown ? NeutralColorSegment.Edge : ColorSegmentColor.VeryLow,
      backgroundColor: isUnknown ? 'palettes.neutral.10.100' : 'palettes.red.10.100',
      label: i18n.t('entity.attractivenessCard.veryLow'),
      secondaryLabel: i18n.t('entity.attractivenessCard.blacklist'),
    },
    {
      key: 'low',
      color: isUnknown ? NeutralColorSegment.NearEdge : ColorSegmentColor.Low,
      label: i18n.t('entity.attractivenessCard.low'),
    },
    {
      key: 'medium',
      color: isUnknown ? NeutralColorSegment.Center : ColorSegmentColor.Medium,
      label: i18n.t('entity.attractivenessCard.medium'),
    },
    {
      key: 'high',
      color: isUnknown ? NeutralColorSegment.NearEdge : ColorSegmentColor.High,
      label: i18n.t('entity.attractivenessCard.high'),
    },
    {
      key: 'veryHigh',
      color: isUnknown ? NeutralColorSegment.Edge : ColorSegmentColor.VeryHigh,
      backgroundColor: isUnknown ? 'palettes.neutral.10.100' : 'palettes.green.10.100',
      label: i18n.t('entity.attractivenessCard.veryHigh'),
      secondaryLabel: i18n.t('entity.attractivenessCard.hitlist'),
    },
  ];

  const getLabelAlignment = (index: number) =>
    match<number, TextAlign>(index)
      .with(0, () => 'left')
      .with(segments.length - 1, () => 'right')
      .otherwise(() => 'center');

  return (
    <Box
      position="relative"
      height={getResponsiveValue(36, 36, 49, 49, 49)}
      ref={chartRef}
      data-testid={suffixTestId('attractivenessChart', props)}
    >
      <VStack height="100%">
        <Grid columns={5} grow={1} horizontalSpacing={0}>
          {segments.map((item, index) => (
            <GridItem key={item.key} data-testid={suffixTestId(`gridItem-${item.key}`, props)}>
              <HStack height="100%">
                <Box width="100%" backgroundColor={item.backgroundColor}>
                  <VStack height="100%" align="center">
                    <Box width="100%" paddingBottom={6}>
                      <Text
                        align={getLabelAlignment(index)}
                        size="xSmall"
                        color="secondary"
                        data-testid={suffixTestId(`label-${item.key}`, props)}
                      >
                        {item.label}
                      </Text>
                      <Show when={item.secondaryLabel}>
                        <Text
                          align={getLabelAlignment(index)}
                          size="xSmall"
                          color="secondary"
                          alternative
                        >
                          {item.secondaryLabel}
                        </Text>
                      </Show>
                    </Box>

                    <Space fillAvailable />

                    <ColorSegment
                      $color={item.color}
                      $isFirst={index === 0}
                      $isLast={isLastIndexInArray(segments, index)}
                      data-testid={suffixTestId(`colorSegment-${item.key}`, props)}
                    />

                    <Space vertical={getResponsiveValue(17, 17, 24, 24, 24)} />
                  </VStack>
                </Box>

                <Show when={!isLastIndexInArray(segments, index)}>
                  <Box paddingBottom={6}>
                    <HStack height="100%">
                      <Separator orientation="vertical" spacing={0} />
                    </HStack>
                  </Box>
                </Show>
              </HStack>
            </GridItem>
          ))}
        </Grid>

        <Box position="absolute" width="100%" bottom={0}>
          <Box position="relative" height={4}>
            {CHART_PERCENTAGE_MARKS.map((item) => (
              <PercentageLabelWrapper key={item} $left={item}>
                <Text
                  size="xSmall"
                  color="secondary"
                  data-testid={suffixTestId(`percentageLabel-${item}`, props)}
                >
                  {item}%
                </Text>
              </PercentageLabelWrapper>
            ))}
          </Box>
        </Box>

        <Box position="absolute" width="100%" bottom={getResponsiveValue(18, 18, 25, 25, 25)}>
          <AttractivenessMarker
            value={props.value}
            segments={segments}
            chartWidth={chartDimensions.width}
            data-testid={suffixTestId('attractivenessMarker', props)}
          />
        </Box>
      </VStack>
    </Box>
  );
}

interface PercentageLabelWrapperProps {
  $left: number;
}

const PercentageLabelWrapper = styled.div<PercentageLabelWrapperProps>`
  position: absolute;
  left: ${({$left: left}) => left}%;
  transform: ${({$left: left}) =>
    match(left)
      .with(0, () => 'none')
      .with(100, () => 'translateX(-100%)')
      .otherwise(() => 'translateX(-50%)')};
`;
